import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Divider,
  Alert,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Preview as PreviewIcon,
  Email as EmailIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import TinyMCEEditor from '../TinyMCEEditor';
import './email-content-fix.css';
import { templatesAPI } from '../../services/api';
import LoadingSpinner from '../LoadingSpinner';



const schema = yup.object({
  name: yup.string().required('Template name is required').max(100, 'Name must be less than 100 characters'),
  type: yup.string().required('Template type is required'),
  subject: yup.string().required('Subject is required').max(200, 'Subject must be less than 200 characters'),
  content: yup.string().required('Template content is required'),
  isActive: yup.boolean(),
  isDefault: yup.boolean()
});

const EmailTemplateForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isView = location.pathname.includes('/view/');
  const isEdit = Boolean(id) && !isView;
  const isCreate = !Boolean(id);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(Boolean(id));
  const [templateData, setTemplateData] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      type: 'proposal',
      subject: '',
      content: '',
      isActive: true,
      isDefault: false
    }
  });

  const watchedContent = watch('content');
  const watchedSubject = watch('subject');

  useEffect(() => {
    if (isEdit || isView) {
      fetchTemplate();
    }
  }, [id, isEdit, isView]);

  const fetchTemplate = async () => {
    try {
      setInitialLoading(true);
      const response = await templatesAPI.getEmailTemplate(id);
      const template = response.data.template; // Fix: access template property
      setTemplateData(template); // Store template data for view mode
      
      // Only reset form for edit mode, not view mode
      if (isEdit) {
        reset({
          name: template.name || '',
          type: template.type || '',
          subject: template.subject || '',
          content: template.content || '',
          isActive: template.isActive !== undefined ? template.isActive : true,
          isDefault: template.isDefault !== undefined ? template.isDefault : false
        });
      }
    } catch (error) {
      console.error('Error fetching template:', error);
      toast.error('Failed to fetch template');
      navigate('/email-templates');
    } finally {
      setInitialLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      
      if (isEdit) {
        await templatesAPI.updateEmailTemplate(id, data);
        toast.success('Email template updated successfully');
      } else {
        await templatesAPI.createEmailTemplate(data);
        toast.success('Email template created successfully');
      }
      
      navigate('/email-templates');
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error(`Failed to ${isEdit ? 'update' : 'create'} email template`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/email-templates');
  };

  const templateTypes = [
    { value: 'proposal', label: 'Proposal' },
    { value: 'reminder', label: 'Reminder' },
    { value: 'welcome', label: 'Welcome' },
    { value: 'notification', label: 'Notification' },
    { value: 'followup', label: 'Follow-up' }
  ];

  const availableVariables = [
    { name: '{{proposal_link}}', description: 'Proposal link' },
    { name: '{{customer_name}}', description: 'Customer name' },
    { name: '{{proposal_title}}', description: 'Proposal title' },
    { name: '{{company_name}}', description: 'Company name' },
    { name: '{{sender_name}}', description: 'Sender name' },
    { name: '{{current_date}}', description: 'Current date' }
  ];



  if (initialLoading) {
    return <LoadingSpinner message="Loading email template..." />;
  }

  // View Mode - Clean, formatted display
  if (isView && templateData) {
    return (
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {templateData.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Chip 
                label={templateData.type || 'General'} 
                color="primary" 
                variant="outlined" 
                size="small"
              />
              <Chip 
                label={templateData.isActive ? 'Active' : 'Inactive'} 
                color={templateData.isActive ? 'success' : 'default'} 
                size="small"
              />
              {templateData.isDefault && (
                <Chip 
                  label="Default Template" 
                  color="secondary" 
                  size="small"
                />
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/email-templates/edit/${id}`)}
            >
              Edit Template
            </Button>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={() => navigate('/email-templates')}
            >
              Back
            </Button>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Email Subject */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Email Subject
                </Typography>
                <Typography variant="body1" sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                  {templateData.subject}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Email Content */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Email Content
                </Typography>
                <Box 
                  className="email-content-display"
                  sx={{ 
                    border: '1px solid', 
                    borderColor: 'divider', 
                    borderRadius: 1, 
                    p: 2,
                    backgroundColor: 'background.paper',
                    minHeight: '200px'
                  }}
                  dangerouslySetInnerHTML={{ __html: templateData.content }}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Template Information */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Template Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Template Type
                    </Typography>
                    <Typography variant="body1">
                      {templateData.type || 'General'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Status
                    </Typography>
                    <Typography variant="body1">
                      {templateData.isActive ? 'Active' : 'Inactive'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Default Template
                    </Typography>
                    <Typography variant="body1">
                      {templateData.isDefault ? 'Yes' : 'No'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Created Date
                    </Typography>
                    <Typography variant="body1">
                      {templateData.createdAt ? new Date(templateData.createdAt).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      }) : 'Unknown'}
                    </Typography>
                  </Grid>
                  {templateData.creator && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Created By
                      </Typography>
                      <Typography variant="body1">
                        {`${templateData.creator.firstName} ${templateData.creator.lastName}`}
                      </Typography>
                    </Grid>
                  )}
                  {templateData.modifier && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Last Modified By
                      </Typography>
                      <Typography variant="body1">
                        {`${templateData.modifier.firstName} ${templateData.modifier.lastName}`}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  // Edit/Create Mode - Form layout
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ 
        mb: 3, 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', md: 'center' },
        gap: { xs: 2, md: 0 }
      }}>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 2 }}>
          <EmailIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            {isEdit ? 'Edit Email Template' : 'Create Email Template'}
          </Typography>
        </Box>
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          flexWrap: 'wrap',
          width: { xs: '100%', md: 'auto' },
          justifyContent: { xs: 'flex-start', md: 'flex-end' }
        }}>
          <Button
            variant="outlined"
            startIcon={<PreviewIcon />}
            onClick={() => setPreviewOpen(true)}
            disabled={!watchedContent}
            size="small"
          >
            Preview
          </Button>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
            disabled={isSubmitting || loading}
            size="small"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || loading}
            startIcon={<SaveIcon />}
            size="small"
            form="email-template-form"
          >
            {isSubmitting || loading ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
          </Button>
        </Box>
      </Box>

      <form id="email-template-form" onSubmit={handleSubmit(onSubmit)}>
        {/* Template Details - Full Width */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Template Details
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Template Name"
                      fullWidth
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      disabled={isView}
                    />
                  )}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.type}>
                      <InputLabel>Template Type</InputLabel>
                      <Select {...field} label="Template Type" disabled={isView}>
                        {templateTypes.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.type && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                          {errors.type.message}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Controller
                  name="subject"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Email Subject"
                      fullWidth
                      error={!!errors.subject}
                      helperText={errors.subject?.message}
                      placeholder="Enter email subject line"
                      disabled={isView}
                    />
                  )}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Email Content
                </Typography>
                <Controller
                  name="content"
                  control={control}
                  render={({ field }) => (
                    <TinyMCEEditor
                       value={field.value || ''}
                       onChange={field.onChange}
                       placeholder="Enter your email template content here..."
                       height={600}
                       error={!!errors.content}
                       helperText={errors.content?.message}
                       disabled={isView}
                     />
                  )}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Controller
                    name="isActive"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} disabled={isView} />}
                        label="Active"
                      />
                    )}
                  />
                  <Controller
                    name="isDefault"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} disabled={isView} />}
                        label="Set as Default"
                      />
                    )}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Bottom Section - Preview on Left, Variables on Right */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            {watchedContent && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Preview
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  {watchedSubject && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Subject:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {watchedSubject}
                      </Typography>
                    </Box>
                  )}
                  <Box
                    className="email-content-display"
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      maxHeight: 400,
                      overflow: 'auto'
                    }}
                    dangerouslySetInnerHTML={{ __html: watchedContent }}
                  />
                </CardContent>
              </Card>
            )}
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Available Variables
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Click on any variable to copy it to your clipboard:
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  {availableVariables.map((variable) => (
                    <Paper
                      key={variable.name}
                      sx={{
                        p: 1.5,
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                      onClick={() => {
                        navigator.clipboard.writeText(variable.name);
                        toast.success('Variable copied to clipboard!');
                      }}
                    >
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {variable.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {variable.description}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </form>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { height: '80vh' }
        }}
      >
        <DialogTitle>
          Email Template Preview
        </DialogTitle>
        <DialogContent>
          {watchedSubject && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Subject:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 2 }}>
                {watchedSubject}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
              backgroundColor: 'background.paper',
              height: '100%',
              overflow: 'auto'
            }}
            dangerouslySetInnerHTML={{ __html: watchedContent }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailTemplateForm;